import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { PackageSearch, Truck, CheckCircle2, Download, Package, CreditCard, Home } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Status timeline: which step index the order has reached
const timelineStep = (r) => {
  const ship = (r.shipment_status || "").toLowerCase();
  if (ship === "delivered" || r.fulfillment_status === "delivered") return 4;
  if (["in_transit", "out_for_delivery"].includes(ship)) return 3;
  if (r.fulfillment_status === "shipped" || r.tracking_number) return 2;
  if (r.payment_status === "paid") return 1;
  return 0;
};

const STEPS = [
  { icon: CreditCard, label: "Order Placed" },
  { icon: Package, label: "Processing" },
  { icon: Truck, label: "Shipped" },
  { icon: PackageSearch, label: "In Transit" },
  { icon: Home, label: "Delivered" },
];

const StatusTimeline = ({ result }) => {
  const current = timelineStep(result);
  return (
    <div data-testid="status-timeline" className="py-2">
      <div className="flex items-center">
        {STEPS.map((s, i) => (
          <div key={s.label} className="flex items-center flex-1 last:flex-none">
            <div className="flex flex-col items-center">
              <div className={`w-8 h-8 flex items-center justify-center border ${
                i < current ? "bg-[#39ff14] border-[#39ff14] text-black"
                : i === current ? "border-[#39ff14] text-[#39ff14] d1-glow"
                : "border-[#22242a] text-[#8e929b]"
              }`}>
                {i < current ? <CheckCircle2 size={14} /> : <s.icon size={14} />}
              </div>
              <span className={`f-mono text-[8px] uppercase tracking-wider mt-1.5 text-center leading-tight ${
                i <= current ? "text-white" : "text-[#8e929b]"
              }`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-px flex-1 mx-1 mb-4 ${i < current ? "bg-[#39ff14]" : "bg-[#22242a]"}`} />
            )}
          </div>
        ))}
      </div>
      <p className="f-body text-[11px] text-[#8e929b] leading-relaxed mt-4 text-center" data-testid="timeline-honest-note">
        {current <= 1 && "Orders are prepared and dropped with USPS the next business day after payment. Tracking is emailed as soon as the label is created."}
        {current === 2 && "Your label is created — the package is scheduled for USPS drop-off the next business day. Tracking activates once USPS scans it in."}
        {current === 3 && "USPS has your package and it's moving. If tracking hasn't updated in a few days, that's usually a missed scan — it almost always keeps moving."}
        {current >= 4 && "Delivered. Anything wrong with the order? Message us from the store chat or email sales@theprintplug.net."}
      </p>
    </div>
  );
};

export default function TrackPage() {
  const [order, setOrder] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const track = async () => {
    if (!order.trim() || !email.trim()) return;
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const { data } = await axios.get(`${API}/track`, {
        params: { order: order.trim(), email: email.trim() },
      });
      setResult(data);
    } catch (e) {
      setError(e.response?.data?.detail || "Order not found");
    }
    setLoading(false);
  };

  return (
    <div className="bg-[#0a0a0c] text-[#ecebe4] min-h-screen flex flex-col">
      <nav className="border-b border-[#22242a] px-6 py-4 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img src="/logo.png" alt="The Print Plug logo" className="w-10 h-10 object-contain" />
          <span className="f-disp tracking-wider text-xl uppercase leading-none text-white">
            THE PRINT PLUG<span className="text-[#39ff14]">.</span>
          </span>
        </Link>
        <Link to="/" data-testid="track-back-link" className="f-mono text-xs uppercase tracking-[0.14em] text-[#8e929b] hover:text-[#39ff14] transition-colors">
          Back to Store
        </Link>
      </nav>

      <main className="flex-1 flex items-start justify-center px-6 py-16 md:py-24">
        <div className="max-w-lg w-full space-y-8">
          <div>
            <p className="f-mono text-xs text-[#39ff14] uppercase tracking-[0.14em] mb-2">Order Lookup</p>
            <h1 className="f-disp text-[clamp(32px,4vw,48px)] leading-[0.95] tracking-[-0.03em] uppercase text-white">
              Track My Order
            </h1>
          </div>

          <div className="bg-[#121316] border border-[#22242a] p-6 space-y-4" data-testid="track-form">
            <input
              data-testid="track-order-input"
              value={order}
              onChange={(e) => setOrder(e.target.value)}
              placeholder="Order number (e.g. PP-10001)"
              className="w-full bg-[#0a0a0c] border border-[#22242a] px-4 py-3 text-sm f-mono uppercase text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
            />
            <input
              data-testid="track-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && track()}
              placeholder="Email used at checkout"
              className="w-full bg-[#0a0a0c] border border-[#22242a] px-4 py-3 text-sm f-body text-white placeholder-[#8e929b] focus:outline-none focus:border-[#39ff14]"
            />
            <button
              data-testid="track-submit-button"
              onClick={track}
              disabled={loading}
              className="w-full py-3.5 bg-[#39ff14] text-black f-mono text-xs uppercase tracking-[0.14em] font-bold hover:bg-white disabled:opacity-60 transition-colors flex items-center justify-center gap-2"
            >
              <PackageSearch size={14} strokeWidth={2.5} />
              {loading ? "Checking…" : "Track Order"}
            </button>
            {error && (
              <p data-testid="track-error" className="f-mono text-[11px] text-red-400 uppercase tracking-wider text-center">{error}</p>
            )}
          </div>

          {result && (
            <div className="bg-[#121316] border border-[#22242a] p-6 space-y-4" data-testid="track-result">
              <div className="flex items-center justify-between border-b border-[#22242a] pb-4">
                <span className="f-mono text-sm font-bold text-white">{result.order_number}</span>
                <span className={`f-mono text-[10px] font-bold px-2 py-1 uppercase flex items-center gap-1.5 ${
                  result.shipment_status === "delivered" || result.fulfillment_status === "delivered"
                    ? "bg-[#39ff14] text-black"
                    : result.shipment_status === "in_transit" || result.shipment_status === "out_for_delivery" || result.fulfillment_status === "shipped"
                    ? "bg-[#f5b700]/15 text-[#f5b700] border border-[#f5b700]/40"
                    : result.shipment_status === "exception"
                    ? "bg-red-500/15 text-red-400 border border-red-500/40"
                    : "bg-[#22242a] text-[#8e929b]"
                }`}>
                  {result.fulfillment_status === "shipped" || result.shipment_status ? <Truck size={11} /> : <CheckCircle2 size={11} />}
                  {result.shipment_status
                    ? result.shipment_status.replace(/_/g, " ")
                    : result.fulfillment_status === "shipped" ? "Shipped" : "Processing"}
                </span>
              </div>
              <StatusTimeline result={result} />
              {(result.label_service || result.delivery_estimate) && (
                <p data-testid="track-estimate" className="f-mono text-[10px] text-[#8e929b] uppercase tracking-wider text-center">
                  {result.label_service}{result.label_service && result.delivery_estimate ? " · " : ""}
                  {result.delivery_estimate && <>ETA <span className="text-[#39ff14]">{result.delivery_estimate}</span></>}
                </p>
              )}
              {result.items.map((i) => (
                <div key={i.variant_id || i.product_id} className="flex justify-between f-mono text-xs">
                  <span className="text-[#ecebe4]">{i.name} × {i.qty}</span>
                  <span className="text-[#39ff14] tabular-nums">${((i.unit_price_cents * i.qty) / 100).toFixed(2)}</span>
                </div>
              ))}
              {result.tracking_number && (
                <a
                  data-testid="track-usps-link"
                  href={result.tracking_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block text-center f-mono text-xs uppercase tracking-[0.14em] px-4 py-3 bg-[#22242a] text-white font-bold hover:bg-[#39ff14] hover:text-black transition-colors"
                >
                  USPS Tracking: {result.tracking_number}
                </a>
              )}
              {result.has_digital && (
                <a
                  data-testid="track-downloads-link"
                  href={`/payment/success?session_id=${result.session_id}`}
                  className="flex items-center justify-center gap-2 f-mono text-xs uppercase tracking-[0.14em] px-4 py-3 bg-[#39ff14] text-black font-bold hover:bg-white transition-colors"
                >
                  <Download size={13} strokeWidth={2.5} />
                  Get My Downloads
                </a>
              )}
              {result.shipped_at && (
                <p className="f-mono text-[10px] text-[#8e929b] uppercase tracking-wider text-center">
                  Shipped {new Date(result.shipped_at).toLocaleDateString("en-US")}
                </p>
              )}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
